"use client";
import { Button } from "@/app/components/ui/button";
import Image from "next/image";
import { AnimatePresence, LazyMotion, m, domAnimation } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { useInView } from "framer-motion";
import aiBgImage from "../../../public/ai-bg.jpg";
import devBgImage from "../../../public/dev-bg.jpg";
import consultBgImage from "../../../public/consult-bg.jpg";

const heroItems = [
  {
    id: 1,
    title: "Empowering You With",
    highlight: "AI-Driven",
    suffix: "Solutions",
    description: "Transform your business with cutting-edge artificial intelligence technology designed to streamline operations and boost productivity.",
    buttonText: "Explore AI Services",
    bgImage: aiBgImage,
    badgeText: "Our AI processes data 10x faster than traditional methods",
    navigateLink: "#services"
  },
  {
    id: 2,
    title: "Building Robust",
    highlight: "Software",
    suffix: "Applications",
    description: "End-to-end development of scalable web and mobile applications with modern technologies and frameworks.",
    buttonText: "View Development Services",
    bgImage: devBgImage,
    badgeText: "We deliver production-ready applications in record time",
    navigateLink: "#technologies"
  },
  {
    id: 3,
    title: "Strategic",
    highlight: "Consulting",
    suffix: "Services",
    description: "Expert guidance to align technology with your business goals and drive digital transformation.",
    buttonText: "Learn About Consulting",
    bgImage: consultBgImage,
    badgeText: "Our consultants have 10+ years industry experience",
    navigateLink: "#consult"
  }
];

const HeroSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.1 });

  // Auto-rotate carousel every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroItems.length);
      setIsFirstRender(false);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const containerVariants = {
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: isFirstRender ? 0 : 0.3
      }
    }
  };

  const itemVariants = {
    visible: {
      y: 0,
      opacity: 1,
      transition: { 
        duration: isFirstRender ? 0.3 : 0.6, 
        ease: "easeOut" 
      }
    }
  };

  const textVariants = {
    enter: { opacity: 0, y: 20 },
    center: { 
      opacity: 1, 
      y: 0,  
      transition: { 
        duration: isFirstRender ? 0.5 : 1, 
        ease: "easeOut" 
      } 
    },
  };

  const bgVariants = {
    enter: { opacity: 1, scale: 1.05 },
    center: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 4, ease: "linear" }
    }
  };

  const buttonVariants = {
    hidden: {
      opacity: 0,
      x: -130,  
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: isFirstRender ? 0.5 : 1, 
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.05,  
      transition: { duration: 0.3 }
    },
    tap: {
      scale: 0.98  
    }
  };

  return (
    <LazyMotion features={domAnimation} strict>
      <section 
        ref={ref}
        className="relative min-h-screen flex items-center pt-16 pb-24 overflow-hidden"
      >
        {/* Background Images with overlay */}
        <AnimatePresence mode="wait">
          <m.div
            key={currentIndex}
            initial="enter"
            animate="center"
            exit="exit"
            variants={bgVariants}
            className="absolute inset-0 z-0"
          >
            <Image
              src={heroItems[currentIndex].bgImage}
              alt={`${heroItems[currentIndex].highlight} background`}
              fill
              className="object-cover"
              quality={90}
              priority={currentIndex === 0}
              loading={currentIndex === 0 ? "eager" : "lazy"}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900/70 to-gray-900/70" />
          </m.div>
        </AnimatePresence>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            {[...Array(20)].map((_, i) => (
              <m.div
                key={i}
                className="absolute rounded-full bg-white"
              />
            ))}
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <m.div 
            className="flex flex-col md:flex-row items-center"
            variants={containerVariants}
            initial="visible"
            animate="visible"
          >
            <div className="md:w-1/2 mb-12 md:mb-0 md:pr-12">
              <AnimatePresence mode="wait">
                <m.div
                  key={currentIndex}
                  initial={isFirstRender ? "center" : "enter"}
                  animate="center"
                  exit="exit"
                  variants={textVariants}
                >
                  <m.h1 
                    className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
                    variants={isFirstRender ? {} : itemVariants}
                  >
                    {heroItems[currentIndex].title}{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-300 to-sky-500">
                      {heroItems[currentIndex].highlight}
                    </span>{' '}
                    {heroItems[currentIndex].suffix}
                  </m.h1>
                  
                  <m.p 
                    className="text-xl text-sky-100 mb-8 max-w-lg"
                    variants={isFirstRender ? {} : itemVariants}
                  >
                    {heroItems[currentIndex].description}
                  </m.p>
                </m.div>
              </AnimatePresence>
              
              <m.div 
                className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
                variants={isFirstRender ? {} : itemVariants}
              >
                <m.div 
                  variants={buttonVariants}
                  initial={isFirstRender ? "visible" : "hidden"}
                  animate="visible"
                  whileHover="hover" 
                  whileTap="tap"
                >
                  <Button
                    size="lg"
                    className="text-white bg-sky-700 hover:bg-sky-800 transition-all transform duration-700 hover:-translate-y-1 hover:shadow-lg"
                  >
                    <a href={heroItems[currentIndex].navigateLink} className="w-full h-full inline-flex items-center justify-center">
                      {heroItems[currentIndex].buttonText}
                    </a>
                  </Button>
                </m.div>
              </m.div>
            </div>
          </m.div>
        </div>
      </section>
    </LazyMotion>
  );
};

export default HeroSection;